import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { VendorService, SearchFormData } from './services/vendor.service';
import { VendorImportService } from './services/vendor-import.service';
import { PermissionService } from '@app/core/services/permission.service';
import { P_PROCUREMENT } from '@app/core/constants/permissions';
import {
  VendorItem,
  VENDOR_SEARCH_CONFIG,
  VENDOR_COLUMNS,
  VENDOR_DEFAULT_COLUMNS_ORDER,
  VENDOR_FLEXIBLE_COLUMNS_ORDER,
  VENDOR_ACTION_COLUMN,
  VENDOR_BUTTON_CONFIG,
  getStatusSeverity,
  getStatusDisplayText,
  getStatusIcon,
  getStatusStyle,
  getCountryDisplayText,
  getPaymentTermDisplayText,
  getPaymentMethodDisplayText,
  getRiskLevelDisplayText,
} from './vendor.config';
import { CountriesService } from '@app/feature/center-business/channel/warehouse/services/countries.service';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';

@Component({
  selector: 'app-vendor',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    MenuModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './vendor.component.html',
  styleUrl: './vendor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorComponent implements OnInit, OnDestroy {
  // Configuration
  searchFormConfig = VENDOR_SEARCH_CONFIG;

  // Check if user has any action permissions (READ, UPDATE, DELETE, or APPROVE)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      VENDOR_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of VENDOR_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(VENDOR_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      VENDOR_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of VENDOR_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Primary button configuration base
  private readonly primaryButtonBase = VENDOR_BUTTON_CONFIG;

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedVendor: VendorItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | null = null;

  // Vendor import (bulk actions): dropdown opens native file picker, then imports
  @ViewChild('vendorSeedFileInput')
  vendorSeedFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('vendorXrefFileInput')
  vendorXrefFileInput?: ElementRef<HTMLInputElement>;
  importLoading = signal(false);

  importMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    if (this.canCreate()) {
      items.push({
        label: this.translateService.instant('vendor.import.option1.title'),
        icon: 'pi pi-file-import',
        command: () => this.onImportOption('seed'),
      });
    }
    if (this.canApprove()) {
      items.push({
        label: this.translateService.instant('vendor.import.option2.title'),
        icon: 'pi pi-box',
        command: () => this.onImportOption('xref'),
      });
    }
    return items;
  });

  templateMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    if (this.canCreate()) {
      items.push({
        label: this.translateService.instant(
          'vendor.download_template.option1.title',
        ),
        icon: 'pi pi-download',
        command: () => this.onDownloadTemplate('seed'),
      });
    }
    if (this.canApprove()) {
      items.push({
        label: this.translateService.instant(
          'vendor.download_template.option2.title',
        ),
        icon: 'pi pi-download',
        command: () => this.onDownloadTemplate('xref'),
      });
    }
    return items;
  });

  // Data signals
  vendors = signal<VendorItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Menu properties
  @ViewChild('actionMenu') actionMenu!: Menu;
  currentMenuModel = signal<any[]>([]);
  selectedRowData: VendorItem | null = null;

  // Search parameters (using 1-based pagination like brand component)
  searchParams = signal({
    code: '',
    name: '',
    phone: '',
    status: undefined as string | undefined,
    page: 1, // Use 1-based pagination
    size: 20,
  });

  // Private properties
  private destroy$ = new Subject<void>();
  private translateService = inject(TranslateService);
  private dateFormatterService = inject(DateFormatterService);
  private readonly permissionService = inject(PermissionService);
  private readonly vendorImportService = inject(VendorImportService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly countriesService = inject(CountriesService);

  // Permission flags - computed from PermissionService
  // Using specific submenu 'vendor' for procurement permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.approve');
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private vendorService: VendorService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Columns are now computed based on permissions, no need to set them here

    // Load countries for country display
    this.countriesService.loadCountries().subscribe();

    this.loadVendors();
    this.setupRouterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load Vendors
  private loadVendors(): void {
    this.loading.set(true);
    const params = {
      ...this.searchParams(),
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
    };
    this.vendorService.getVendors(params).subscribe({
      next: (response) => {
        this.vendors.set(response.data as VendorItem[]);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading Vendors:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'vendor.messages.error.failed_to_load_vendors',
          ),
        });
      },
    });
  }

  private setupRouterListener(): void {
    // Listen for navigation events to refresh data when returning to this page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        // Check if we're navigating to the vendor list page
        if (
          event.url.includes('/procurement/vendor') &&
          !event.url.includes('/add') &&
          !event.url.includes('/edit') &&
          !event.url.includes('/view')
        ) {
          // Refresh data when returning to list page
          this.loadVendors();
        }
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), ...searchData, page: 1 });
    this.loadVendors();
  }

  onReset(): void {
    this.searchParams.set({
      code: '',
      name: '',
      phone: '',
      status: undefined,
      page: 1,
      size: 20,
    });
    this.loadVendors();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadVendors();
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/vendor/add']);
  }

  onImportOption(mode: 'seed' | 'xref'): void {
    if (mode === 'seed' && !this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (mode === 'xref' && !this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    const input =
      mode === 'seed'
        ? this.vendorSeedFileInput?.nativeElement
        : this.vendorXrefFileInput?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onDownloadTemplate(mode: 'seed' | 'xref'): void {
    if (mode === 'seed' && !this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (mode === 'xref' && !this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    const fileName =
      mode === 'seed'
        ? 'vendor_creation_template.csv'
        : 'vendor_product_mapping_template.csv';
    const url = `assets/templates/vendor/${fileName}`;

    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'vendor.download_template.messages.download_error',
        ),
      });
    }
  }

  async onVendorSeedFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.importLoading.set(true);
    try {
      const result = await this.vendorImportService.importVendorSeed(file);
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'vendor.import.messages.seed_success',
          { count: result.rowCount, skipped: result.skippedCount },
        ),
      });
      this.loadVendors();
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.getImportErrorDetail(e, 'vendor.import.messages.seed_error'),
      });
    } finally {
      this.importLoading.set(false);
    }
  }

  async onVendorXrefFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.importLoading.set(true);
    try {
      const result = await this.vendorImportService.importFm07Xref(file);
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'vendor.import.messages.xref_success',
          {
            created: result.createdCount,
            skipped: result.skippedCount,
          },
        ),
      });
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.getImportErrorDetail(e, 'vendor.import.messages.xref_error'),
      });
    } finally {
      this.importLoading.set(false);
    }
  }

  private getImportErrorDetail(error: any, fallbackKey: string): string {
    if (error?.name === 'VendorImportFormatError' || error?.message === 'INVALID_CSV_FORMAT') {
      const missing = Array.isArray(error?.missingColumns)
        ? error.missingColumns.join(', ')
        : '';
      return this.translateService.instant(
        'vendor.import.messages.invalid_format',
        { columns: missing },
      );
    }
    return (
      error?.error?.message ??
      error?.message ??
      this.translateService.instant(fallbackKey)
    );
  }

  // Get available actions for a vendor based on its status
  getVendorActions(vendor: VendorItem): string[] {
    /**
     * Workflow:
     * DRAFT:      Edit, Delete, Submit for Review (REVIEWING)
     * REVIEWING:  Approve (APPROVED), Reject (REJECTED)
     * REJECTED:   Edit, Delete (can resubmit)
     * APPROVED:   Activate (ACTIVE)
     * ACTIVE:     Deactivate (INACTIVE), Manage SKU
     * INACTIVE:   Activate (ACTIVE)
     *
     * Note: Edit and Delete are only available for DRAFT and REJECTED statuses
     * Note: Manage SKU is only available for ACTIVE vendors
     */
    const status = vendor.status;
    const actions: string[] = [];
    switch (status) {
      case 'DRAFT':
        actions.push('edit', 'delete', 'submit_for_review');
        break;
      case 'REVIEWING':
        actions.push('approve', 'reject');
        break;
      case 'REJECTED':
        actions.push('edit', 'delete');
        break;
      case 'APPROVED':
        actions.push('activate');
        break;
      case 'ACTIVE':
        actions.push('manage_vendor_sku', 'deactivate');
        break;
      case 'INACTIVE':
        actions.push('activate');
        break;
      default:
        break;
    }
    return actions;
  }

  // Check if vendor has any enabled actions (excluding view)
  hasEnabledVendorActions(vendor: VendorItem): boolean {
    const actions = this.getVendorActions(vendor);
    return actions.some((action) => {
      if (action === 'view') return false; // View is handled separately
      if (action === 'edit') return this.canUpdate();
      if (action === 'delete') return this.canDelete();
      if (
        [
          'approve',
          'reject',
          'submit_for_review',
          'activate',
          'deactivate',
          'manage_vendor_sku',
        ].includes(action)
      ) {
        return this.canApprove();
      }
      return true;
    });
  }

  // Toggle menu for actions
  toggleMenu(event: Event, rowData: VendorItem): void {
    this.selectedRowData = rowData;
    const actions = this.getVendorActions(rowData);

    // Create menu items based on available actions with permission checks
    // Filter out actions that user doesn't have permission for
    const menuItems = actions
      .filter((action) => {
        // Check permissions for each action - only include if user has permission
        if (action === 'view') {
          return this.canRead();
        } else if (action === 'edit') {
          return this.canUpdate();
        } else if (action === 'delete') {
          return this.canDelete();
        } else if (
          [
            'approve',
            'reject',
            'submit_for_review',
            'activate',
            'deactivate',
            'manage_vendor_sku',
          ].includes(action)
        ) {
          return this.canApprove();
        }
        return true;
      })
      .map((action) => ({
        label: this.translateService.instant(`common.actions.${action}`),
        command: () => this.onActionClick(action, rowData),
      }));

    this.currentMenuModel.set(menuItems);

    // Show menu at cursor position
    this.actionMenu.toggle(event);
  }

  // Handle action clicks
  onActionClick(action: string, rowData: VendorItem): void {
    switch (action) {
      case 'view':
        this.onView(rowData);
        break;
      case 'edit':
        this.onEdit(rowData);
        break;
      case 'submit_for_review':
        this.onSubmitForReview(rowData);
        break;
      case 'approve':
        this.onApprove(rowData);
        break;
      case 'reject':
        this.onReject(rowData);
        break;
      case 'activate':
        this.onActivate(rowData);
        break;
      case 'deactivate':
        this.onDeactivate(rowData);
        break;
      case 'manage_vendor_sku':
        this.onManageVendorSku(rowData);
        break;
      case 'delete':
        this.onDelete(rowData);
        break;
    }
  }

  onView(vendor: VendorItem): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/vendor/view', vendor.id]);
  }

  onEdit(vendor: VendorItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/vendor/edit', vendor.id]);
  }

  // Status-specific action methods
  onSubmitForReview(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updateVendorStatus(vendor, 'REVIEWING');
  }

  onApprove(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updateVendorStatus(vendor, 'APPROVED');
  }

  onReject(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updateVendorStatus(vendor, 'REJECTED');
  }

  onActivate(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updateVendorStatus(vendor, 'ACTIVE');
  }

  onDeactivate(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updateVendorStatus(vendor, 'INACTIVE');
  }

  onManageVendorSku(vendor: VendorItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/vendor', vendor.id, 'manage-sku']);
  }

  // Update vendor status
  private updateVendorStatus(
    vendor: VendorItem,
    status:
      | 'DRAFT'
      | 'REVIEWING'
      | 'REJECTED'
      | 'APPROVED'
      | 'ACTIVE'
      | 'INACTIVE',
  ): void {
    const vendorId = Number.parseInt(vendor.id);
    if (isNaN(vendorId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'vendor.messages.error.invalid_vendor_id',
        ),
      });
      return;
    }

    this.vendorService.changeStatus(vendorId, status).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'vendor.messages.status_updated',
            { status: this.getStatusDisplayText(status) },
          ),
        });

        // Refresh the vendors list
        this.loadVendors();
      },
      error: (error) => {
        console.error('Error updating vendor status:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'vendor.messages.status_update_failed',
          ),
        });
      },
    });
  }

  onDelete(vendor: VendorItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedVendor = vendor;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'vendor.messages.confirmation.delete_vendor',
      { name: vendor.name || vendor.code },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private executeConfirmedAction(): void {
    if (!this.selectedVendor) return;

    switch (this.currentAction) {
      case 'delete':
        this.confirmDelete();
        break;
    }
  }

  private confirmDelete(): void {
    if (this.selectedVendor) {
      this.vendorService
        .deleteVendor(this.selectedVendor.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'vendor.messages.success.vendor_deleted',
              ),
            });
            this.loadVendors(); // Refresh data
          },
          error: (error) => {
            console.error('Error deleting Vendor:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'vendor.messages.error.failed_to_delete_vendor',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedVendor = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Status methods
  getStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return getStatusDisplayText(status, this.translateService);
  }

  getStatusIcon(status: string): string {
    return getStatusIcon(status);
  }

  getStatusStyle(status: string): any {
    return getStatusStyle(status);
  }

  // Date formatting using DateFormatterService
  formatDate(dateInput: any): string {
    if (!dateInput) return '';
    return this.dateFormatterService.formatDateTime(dateInput);
  }

  // Get vendor type display text
  getVendorTypeDisplayText(vendorType: string): string {
    if (!vendorType) return '';
    const typeMap: { [key: string]: string } = {
      COMPANY: 'vendor.form.vendor_type.company',
      INDIVIDUAL: 'vendor.form.vendor_type.individual',
      PARTNERSHIP: 'vendor.form.vendor_type.partnership',
    };
    const translationKey = typeMap[vendorType] || vendorType;
    return this.translateService.instant(translationKey);
  }

  // Get country display text
  getCountryDisplayText(countryCode: string): string {
    return getCountryDisplayText(
      countryCode,
      this.countriesService,
      this.translateService,
    );
  }

  // Get payment term display text
  getPaymentTermDisplayText(paymentTerm: string): string {
    return getPaymentTermDisplayText(paymentTerm, this.translateService);
  }

  // Get payment method display text
  getPaymentMethodDisplayText(paymentMethod: string): string {
    return getPaymentMethodDisplayText(paymentMethod, this.translateService);
  }

  // Get risk level display text
  getRiskLevelDisplayText(riskLevel: string): string {
    return getRiskLevelDisplayText(riskLevel, this.translateService);
  }

  // Handle confirmation dialog button clicks
  onConfirmatiButtonClick(action: string): void {
    if (
      action === 'confirm' &&
      this.currentAction === 'delete' &&
      this.selectedVendor
    ) {
      this.executeDeleteVendor(this.selectedVendor);
    }
    this.showConfirmationDialog = false;
    this.selectedVendor = null;
    this.currentAction = null;
  }

  // Execute delete vendor
  private executeDeleteVendor(vendor: VendorItem): void {
    this.vendorService.deleteVendor(vendor.id).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'vendor.messages.deleted_successfully',
          ),
        });

        // Refresh the vendors list
        this.loadVendors();
      },
      error: (error) => {
        console.error('Error deleting vendor:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'vendor.messages.delete_failed',
          ),
        });
      },
    });
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }
}
